import { SVGProps } from 'react';
import { BikePartType } from '../../model/BikePartType';
import { Field } from '../../model/parts/Field';
import { PartDetailForm } from '../../model/parts/PartDetailForm';
import { PartsDetailForm } from '../../model/parts/PartsDetailForm';

interface PartToEditInfo {
  label: string;
  name: BikePartType;
  part?: PartDetailForm;
  fields?: Field[];
  dropdown?: Field;
  additionalDropdown?: Field;
}

interface Props {
  onClick: (value: PartToEditInfo) => void;
  form: Partial<PartsDetailForm>;
  prop?: SVGProps<SVGSVGElement>;
  havePowerMeter: boolean | undefined;
}

const getAddIcon = (isAdded: boolean) => {
  return (
    <>
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        fill={isAdded ? 'green' : 'white'}
      />
      <path
        d="M12 8V16"
        stroke={isAdded ? 'white' : '#2238CB'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 12H16"
        stroke={isAdded ? 'white' : '#2238CB'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
};

const BikeSvgComponent = (props: Props) => (
  <svg
    width={'21em'}
    height={'18.18em'}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props.prop}
  >
    <g id="saddle">
      <svg
        x={'4em'}
        y={'0.1em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              { label: 'Width (millimeters)', name: 'measurement', inputType: 'number' },
              { name: 'material' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Saddle',
            name: BikePartType.saddle,
            part: props.form.saddle,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.saddle !== undefined)}
      </svg>
    </g>
    <g id="seatpost">
      <svg
        x={'4.8em'}
        y={'2.5em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              { name: 'material' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
              {
                label: 'Diameter (millimeters)',
                name: 'measurement',
                inputType: 'number',
              },
            ],
            label: 'Seatpost',
            name: BikePartType.seatpost,
            part: props.form.seatpost,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.seatpost !== undefined)}
      </svg>
    </g>
    <g id="stem">
      <svg
        x={'13em'}
        y={'0.4em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              { name: 'material' },
              {
                name: 'additionalInfo',
                label: 'Additional Information',
                displayAfterDropDown: true,
              },
            ],
            dropdown: { label: 'Length (millimeters)', name: 'stem' },
            label: 'Stem',
            name: BikePartType.stem,
            part: props.form.stem,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.stem !== undefined)}
      </svg>
    </g>
    <g id="handlebar">
      <svg
        x={'14.8em'}
        y={'1.2em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              { name: 'material', required: true },
              {
                name: 'additionalInfo',
                label: 'Additional Information',
                displayAfterDropDown: true,
              },
            ],
            dropdown: { label: 'Width (centimeters)', name: 'handlebar', required: true },
            label: 'Handlebar',
            name: BikePartType.handlebar,
            part: props.form.handlebar,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.handlebar !== undefined)}
      </svg>
    </g>
    <g id="tires">
      <svg
        x={'0.3em'}
        y={'6.5em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              {
                label: 'Width (millimeters)',
                name: 'measurement',
                inputType: 'number',
              },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Tires',
            name: BikePartType.tires,
            part: props.form.tires,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.tires !== undefined)}
      </svg>
    </g>
    <g id="brake">
      <svg
        x={'4.5em'}
        y={'8.5em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            dropdown: { label: 'Type', name: 'brake', required: true },
            label: 'Brakes *',
            name: BikePartType.brake,
            part: props.form.brake,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.brake !== undefined)}
      </svg>
    </g>
    <g id="cassette">
      <svg
        x={'2.2em'}
        y={'9.5em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            dropdown: { label: 'Size', name: 'cassette', required: true },
            label: 'Cassette *',
            name: BikePartType.cassette,
            part: props.form.cassette,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.cassette !== undefined)}
      </svg>
    </g>

    <g id="wheelset">
      <svg
        x={'16.3em'}
        y={'8.35em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand', required: true },
              { name: 'model' },
              { name: 'material' },
              {
                label: 'Depth (millimeters)',
                name: 'measurement',
                inputType: 'number',
              },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Wheelset *',
            name: BikePartType.wheelset,
            part: props.form.wheelset,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.wheelset !== undefined)}
      </svg>
    </g>
    <g id="power meter">
      <svg
        x={'8.2em'}
        y={'13.1em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand', required: true },
              { name: 'model' },
              { name: 'material', displayAfterDropDown: true },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            dropdown: { label: 'Type', name: 'power-meter', required: props.havePowerMeter },
            label: props.havePowerMeter ? 'Power Meter *' : 'Power Meter',
            name: BikePartType.powerMeter,
            part: props.form.powerMeter,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.powerMeter !== undefined)}
      </svg>
    </g>
    <g id="bottom bracket">
      <svg
        x={'6.7em'}
        y={'13.4em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand', required: true },
              { name: 'model', required: true },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Bottom bracket',
            name: BikePartType.bottomBracket,
            part: props.form.bottomBracket,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.bottomBracket !== undefined)}
      </svg>
    </g>
    <g id="rear derailleur">
      <svg
        x={'3.2em'}
        y={'12.4em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Rear derailleur',
            name: BikePartType.rearDerailleur,
            part: props.form.rearDerailleur,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.rearDerailleur !== undefined)}
      </svg>
    </g>
    <g id="front derailleur">
      <svg
        x={'7.3em'}
        y={'11em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Front Derailleur',
            name: BikePartType.frontDerailleur,
            part: props.form.frontDerailleur,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.frontDerailleur !== undefined)}
      </svg>
    </g>
    <g id="crankset">
      <svg
        x={'7.7em'}
        y={'14.5em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            dropdown: { label: 'Length (millimeters)', name: 'crankset', required: true },
            additionalDropdown: { label: 'Chainring size', name: 'chainring-size', required: true },
            label: 'Crankset',
            name: BikePartType.crankset,
            part: props.form.crankset,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.crankset !== undefined)}
      </svg>
    </g>
    <g id="pedal">
      <svg
        x={'10em'}
        y={'16em'}
        onClick={() =>
          props.onClick!({
            fields: [
              { name: 'brand' },
              { name: 'model' },
              { name: 'material' },
              {
                label: 'Additional Information',
                name: 'additionalInfo',
                displayAfterDropDown: true,
              },
            ],
            label: 'Pedals',
            name: BikePartType.pedals,
            part: props.form.pedals,
          })
        }
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {getAddIcon(props.form.pedals !== undefined)}
      </svg>
    </g>
    <g clipPath="url(#a)" stroke="#fff" strokeOpacity={0.3}>
      <path
        d="M109.838 200.102c-4.723 31.353-23.576 55.215-47.943 57.198-30.387 2.487-57.84-30.01-61.32-72.582-3.479-42.572 18.337-79.085 48.723-81.585 15.276-1.249 29.815 6.356 40.935 19.613M93 126.267c9.507 12.846 16.038 30.111 17.617 49.456.451 5.475.483 10.976.098 16.456M219.829 124.127a59.014 59.014 0 0 1 25.21-9.473c39-4.996 75.643 30.276 81.848 78.784 6.205 48.508-20.385 91.88-59.381 96.88-38.996 4.999-75.646-30.277-81.847-78.806-4.182-32.695 6.535-63.061 25.908-80.924M85.843 37.462 83.22 26.573c-.148-.606 3.069-1.872 3.428-1.978 9.313-2.746 11.386 11.63 12.546 17.735.182.958.352 1.915.53 2.869.901 4.804 6.23 28.84 6.23 28.84L98 75.539l-2.81-12.923c-1.372-6.327-2.745-12.652-4.12-18.977-.634-2.936-1.54-6.275-5.227-6.177Z"
        strokeMiterlimit={10}
      />
      <path
        d="M202.928 106.036a101.445 101.445 0 0 0-2.18 3.08l-2.404 3.491-3.153 4.576-3.784 5.508-4.318 6.266-4.732 6.868-5.037 7.322-5.24 7.626-5.329 7.736-5.312 7.712-5.198 7.499-4.943 7.202-4.601 6.678-4.14 6.016-3.577 5.194-2.903 4.216-2.116 3.076-1.228 1.784-.271.39-85.234-30.307s-3.293-2.148-.262-6.075c2.163-2.797 28.359-32.349 43.254-49.118 6.01-6.779 10.158-11.44 10.158-11.44s2.383-2.686 3.933-2.61l-4.982-24.395 8.067-1.526 3.551.17s71.194-17.825 81.234-17.748l17.122-4.237s2.281 3.99 4.558 16.32c2.277 12.33 5.079 22.125 5.079 22.125l-3.259.695c-1.947.423-3.89.847-5.828 1.271-1.803.424-3.602.669-4.902 2.118-.69.784-1.371 1.64-2.023 2.517Z"
        strokeMiterlimit={10}
      />
      <path
        d="M133.508 184.481c.63-.911 1.27-1.817 1.888-2.728l2.649-3.813 3.302-4.758 3.839-5.542 4.271-6.161 4.588-6.622 4.804-6.927 4.901-7.076c1.634-2.35 3.267-4.702 4.898-7.058l4.778-6.889 4.555-6.559 4.232-6.067a3107.36 3107.36 0 0 1 3.767-5.423l3.213-4.623 2.54-3.652 1.773-2.542.677-.958c4.614-6.478 5.177-9.232 5.08-11.66-.043-1.334-.716-2.711-1.245-3.91-2.142-4.868-3.466-5.974-8.888-5.974a81.151 81.151 0 0 0-9.346.64c-3.746.423-7.471 1.03-11.187 1.695a419.596 419.596 0 0 0-12.339 2.393c-4.27.886-8.533 1.818-12.795 2.763l-11.145 2.487c-2.827.631-5.646 1.3-8.44 2.055-1.981.534-3.957 1.105-5.9 1.766-1.294.428-2.565.92-3.809 1.475-1.774.81-3.42 1.406-3.268 3.72.144 2.156 1.113 4.66 1.57 6.817l1.147 5.372 1.367 6.389 1.533 7.178 1.65 7.732 1.723 8.05 1.74 8.147 1.693 8.004 1.629 7.626 1.507 7.033 1.325 6.203 1.096 5.135.822 3.843c.266 1.237.321 2.665 1.13 3.703 1.223 1.504 2.061-.411 2.675-1.284ZM86.309 145.502l-15.636 26.205s-2.17 1.271 4.99 5.084c7.163 3.813 20.047 10.8 20.047 10.8"
        strokeMiterlimit={10}
      />
      <path
        d="M123.044 195.607s-62.885-17.85-67.888-19.599c-5.003-1.75-1.219-6.25-1.219-6.25l50.577-56.168s.986-.203.376.797c-.207.335-4.19 7.012-9.375 15.676"
        strokeMiterlimit={10}
      />
      <path
        d="m110.909 187.65 10.967 4.945s.847.377.893-.674c.047-1.051.758-8.749-.342-14.939-1.101-6.19-4.453-22.951-5.401-25.133-.949-2.182-7.59-21.506-8.203-24.858-.614-3.351-1.27-5.974-1.27-5.974s-.25-.847-.58-.271l-8.427 14.274M89.039 151.095l-11.238 19.036s-1.27 1.695.305 2.584c.665.377 8.55 3.97 17.447 8M224.663 142.329a44.388 44.388 0 0 1 20.85-8.508c29.672-3.813 57.637 23.726 62.466 61.477 4.83 37.75-15.309 71.442-44.98 75.246-29.672 3.805-57.637-23.726-62.462-61.476-3.052-23.85 3.86-46.076 16.846-60.164"
        strokeMiterlimit={10}
      />
      <path
        d="M94.74 194.912c-3.386 23.625-17.197 41.606-35.076 43.072-22.29 1.822-42.483-22.714-45.104-54.808-2.62-32.094 13.32-59.587 35.606-61.434 10.704-.873 20.922 4.33 28.943 13.528M81.869 138.723c7.195 9.745 12.186 22.989 13.4 37.848.308 3.754.363 7.525.166 11.287M229.437 119.005l-2.311-6.436s-3.437-9.486-7.817-8.05c-4.381 1.436-.949 13.516-.949 13.516l20.842 78.958a5.576 5.576 0 0 1-6.637 0l-25.582-81.458s-1.879-7.626 1.693-8.694c4.093-1.246 10.967-2.419 10.967-2.419"
        strokeMiterlimit={10}
      />
      <path
        d="m227.537 140.278 23.28 53.101s2.599 1.83 5.045-.958l-.885-2.292-19.356-53.922M65.721 6.643c-.318 0-.63.034-.94.076-2.116.276-1.921 2.704-1.693 4.288.377 2.508 2.193 4.385 4.3 5.635 5.545 3.292 13.596-3.144 21.164-3.88 7.568-.738 13.452-1.179 15.513.05 1.342.8 3.179 1.402 4.715.8 1.397-.546 1.232-2.22.618-3.32-.55-.988-1.693-1.31-2.734-1.412-2.54-.245-16.884.886-23.365-.292-3.162-.576-6.4-1.182-9.62-1.555a52.1 52.1 0 0 0-4.462-.297c-1.13-.03-2.34-.14-3.496-.093ZM187.292 13.994a9.929 9.929 0 0 0-3.597 1.344c-1.224.78-2.942 2.118-2.426 3.792.271.88 1.156 1.86 2.117 1.974 1.413.17 2.831.318 4.232.47l2.117.225c.571.059 1.104 0 1.591.322.902.606 1.549 1.542 2.349 2.27.379.35.781.672 1.202.967.382.192.74.428 1.067.703.289.387.468.845.516 1.326.327 1.701.469 3.433.424 5.165a18.31 18.31 0 0 1-1.566 6.75 18.96 18.96 0 0 1-5.503 6.948c-3.318 2.665-7.322 4.372-11.386 5.537-.614.178-1.236.343-1.858.496-.88.216-1.49.347-2.006 1.195a5.766 5.766 0 0 0-.796 3.334 3.71 3.71 0 0 0 .698 1.86c.244.325.528.617.847.869.3.292.652.526 1.037.69.462.08.935.067 1.393-.038a34.15 34.15 0 0 0 5.117-1.207 38.675 38.675 0 0 0 6.59-2.64 30.948 30.948 0 0 0 6.87-4.741 25.885 25.885 0 0 0 5.625-7.444 28.827 28.827 0 0 0 2.857-10.732 41.594 41.594 0 0 0-.339-8.948c-.224-1.602.576-3.06 1.694-4.165a10.182 10.182 0 0 1 3.034-2.229c.424-.19 1.063-.53 1.427-.089.296.352.283.962.292 1.394a6.123 6.123 0 0 1-.326 2.009 20.026 20.026 0 0 1-1.757 3.643c-.635 1.136-1.536 2.119-1.811 3.432a6.388 6.388 0 0 0-.115 1.767c.136 2.097 1.088 4.084 2.235 5.808.311.464.637.913.978 1.348a48.555 48.555 0 0 0 3.416 4.033 7.522 7.522 0 0 0 2.315 1.847c.754.322 2.201.424 1.994-.817-.182-1.098-1.27-1.763-1.579-2.826a12.69 12.69 0 0 1-.423-2.907 65.26 65.26 0 0 1 .038-6.634c0-.327.021-.657.029-.983.081-3.195 1.499-9.148 1.342-11.753-.156-2.606-1.892-3.865-1.066-8.084.211-1.077.423-2.153.626-3.237.203-1.085.423-2.212-.262-3.165a6.462 6.462 0 0 0-1.452-1.403 7.413 7.413 0 0 0-6.04-1.025c-5.003 1.182-.788 7.016-6.696 10.529a19.847 19.847 0 0 1-7.133 2.39c-2.611.393-5.362.16-7.932.63ZM246.089 21.159s3.25-2.254.465-4.843c0 0 1.27-1.326 6.036-1.394 4.766-.068 14.692.542 18.408-4.423 3.716-4.966 1.164-8.025 6.701-8.05 5.536-.026 4.766 4.576 4.571 6.126-.195 1.551-.847 3.915.148 6.28.995 2.363 2.747 3.651 2.54 12.62a38.686 38.686 0 0 0 .677 8.818c.177.868.411 1.725.698 2.563.322.924.969 1.797 1.156 2.75.169.89-.352 1.33-1.075.733a28.514 28.514 0 0 1-4.931-5.576c-1.512-2.118-3.543-4.766-2.591-7.55.394-1.157 1.016-2.3 1.202-3.52.186-1.221-.245-2.306-.652-3.39-.406-1.085-.423-2.055-.567-3.174-.089-.69-.58-.77-1.16-.792-1.553-.055-3.297 1.271-4.461 2.148a9.992 9.992 0 0 0-1.875 1.788c-1.041 1.369-.195 3.317-.059 4.851.23 2.519.18 5.055-.148 7.563a26.552 26.552 0 0 1-2.963 9.21 24.681 24.681 0 0 1-7.936 8.572c-4.233 2.834-9.13 4.385-14.057 5.436-.754.161-1.554.483-2.332.318-.779-.166-1.304-.776-1.804-1.322-.952-1.055-1.024-2.58-.643-3.898a2.44 2.44 0 0 1 .978-1.424c.846-.525 2.002-.423 2.929-.707 1.206-.36 2.408-.687 3.589-1.131 4.415-1.653 8.593-4.271 11.428-8.105a18.523 18.523 0 0 0 2.963-6.17c.51-1.674.777-3.413.792-5.164a7.817 7.817 0 0 0-1.35-3.936c-.728-1.174-1.473-2.479-2.76-3.119-1.524-.758-3.513-.449-5.164-.52a43.692 43.692 0 0 1-5.858-.547c-.961-.17-2.133-.364-2.895-1.021Z"
        strokeMiterlimit={10}
      />
      <path
        d="M246.118 16.638s2.54 1.94-.52 4.203c0 0-20.74 2.085-21.468 2.745 0 0 1.185-4-1.215-7.122 0 0 3.06 1 9.409.801 6.349-.199 13.794-.627 13.794-.627Z"
        strokeMiterlimit={10}
      />
      <path
        d="M152.432 246.666a1.536 1.536 0 0 1-.317-.322 1.304 1.304 0 0 1-.254-.903c.038-.344.186-.667.423-.919.109-.128.232-.242.368-.339v0a1.57 1.57 0 0 1-.343-.297 1.32 1.32 0 0 1-.326-.877 1.95 1.95 0 0 1 .695-1.322v0a1.45 1.45 0 0 1-.364-.263 1.367 1.367 0 0 1-.424-.847 1.723 1.723 0 0 1 .275-.974c.085-.146.189-.28.309-.399v0a1.415 1.415 0 0 1-.846-1.042 1.733 1.733 0 0 1 .182-1c.076-.151.17-.294.279-.423a1.641 1.641 0 0 1-.402-.2 1.27 1.27 0 0 1-.546-.766 1.647 1.647 0 0 1 .093-1.009c.063-.158.143-.308.237-.449v0a1.53 1.53 0 0 1-.423-.157 1.332 1.332 0 0 1-.61-.711 1.7 1.7 0 0 1 0-1.013c.044-.164.107-.321.187-.47v0a1.603 1.603 0 0 1-.424-.119 1.36 1.36 0 0 1-.681-.644 1.759 1.759 0 0 1-.11-1.008c.027-.168.074-.331.14-.488v0a1.646 1.646 0 0 1-.424-.067 1.276 1.276 0 0 1-.745-.568 1.6 1.6 0 0 1-.228-.987c.007-.168.032-.334.076-.496v0c-.15.022-.303.022-.453 0a1.344 1.344 0 0 1-.808-.47 1.759 1.759 0 0 1-.347-.949 2.28 2.28 0 0 1 0-.509v0a1.442 1.442 0 0 1-1.317-.309 1.725 1.725 0 0 1-.478-.894 2.277 2.277 0 0 1-.059-.5v0a1.412 1.412 0 0 1-.424.106 1.294 1.294 0 0 1-.905-.221 1.69 1.69 0 0 1-.61-.804 2.668 2.668 0 0 1-.14-.488c-.131.08-.274.139-.423.174-.138.038-.28.054-.423.047a1.145 1.145 0 0 1-.508-.115 1.815 1.815 0 0 1-.732-.69 2.49 2.49 0 0 1-.216-.462v0a1.62 1.62 0 0 1-.385.25 1.374 1.374 0 0 1-.927.093 1.782 1.782 0 0 1-.847-.555 2.352 2.352 0 0 1-.296-.424v0c-.095.117-.206.22-.33.305a1.27 1.27 0 0 1-.898.255 1.629 1.629 0 0 1-.927-.394 2.22 2.22 0 0 1-.364-.348v0a1.53 1.53 0 0 1-.27.364 1.382 1.382 0 0 1-.847.424 1.772 1.772 0 0 1-.982-.229 2.365 2.365 0 0 1-.423-.283v0a1.501 1.501 0 0 1-.203.423 1.404 1.404 0 0 1-.754.547 1.827 1.827 0 0 1-1.007-.06 2.321 2.321 0 0 1-.462-.211 1.618 1.618 0 0 1-.135.423c-.13.295-.366.531-.66.661a1.63 1.63 0 0 1-1.008.093 2.242 2.242 0 0 1-.482-.135 1.599 1.599 0 0 1-.072.445c-.09.309-.289.575-.559.75a1.699 1.699 0 0 1-.982.233 2.268 2.268 0 0 1-.504-.077v0c.023.151.023.303 0 .454-.05.318-.214.607-.461.813a1.657 1.657 0 0 1-.948.356 1.992 1.992 0 0 1-.5 0 1.442 1.442 0 0 1-.317 1.314 1.69 1.69 0 0 1-.902.461 2.23 2.23 0 0 1-.503.043v0c.086.22.116.459.086.694-.031.234-.12.458-.26.649-.216.27-.512.465-.846.555-.16.05-.325.083-.491.097.067.134.112.277.135.424.057.317-.005.644-.173.919a1.751 1.751 0 0 1-.792.636c-.156.065-.32.112-.487.14v0a1.418 1.418 0 0 1 .089 1.347 1.69 1.69 0 0 1-.732.703 1.976 1.976 0 0 1-.47.183c.087.123.158.257.212.398.12.302.12.638 0 .94a1.61 1.61 0 0 1-.665.763 2.361 2.361 0 0 1-.453.225v0a1.383 1.383 0 0 1 .322 1.313 1.58 1.58 0 0 1-.601.818c-.132.102-.274.19-.423.262v0c.109.106.202.227.275.361a1.3 1.3 0 0 1 .148.923c-.084.333-.273.63-.538.847a1.96 1.96 0 0 1-.423.293v0c.119.095.221.208.305.335.182.266.26.59.22.911a1.64 1.64 0 0 1-.466.902 1.98 1.98 0 0 1-.381.326c.126.086.237.19.331.309.207.249.311.567.292.89a1.64 1.64 0 0 1-.39.937 2.174 2.174 0 0 1-.355.356v0c.13.075.248.17.351.279.217.231.346.531.364.847a1.696 1.696 0 0 1-.313.966 1.913 1.913 0 0 1-.326.386v0c.136.064.262.148.372.25.241.22.392.522.424.847.023.346-.056.691-.229.992a2.15 2.15 0 0 1-.296.424c.141.052.273.125.393.216.265.189.447.472.508.792.06.341.013.691-.135 1.004-.07.15-.157.293-.258.424v0a1.392 1.392 0 0 1 .994.919 1.59 1.59 0 0 1-.046 1.013c-.051.16-.121.314-.208.457v0c.148.026.29.073.424.14.289.14.518.381.643.678.123.324.143.677.059 1.013a2.04 2.04 0 0 1-.161.474v0c.145.013.288.046.424.098.307.103.563.32.715.605.16.306.22.655.169.996a2.633 2.633 0 0 1-.11.496v0c.153-.008.305.007.453.042.313.073.59.257.779.517.189.288.289.626.288.97.003.17-.013.339-.047.504v0c.149-.022.3-.022.449 0 .326.027.629.179.846.424.235.256.383.579.424.924.025.167.034.336.025.504v0c.137-.047.279-.072.423-.076.321-.011.636.091.889.288.258.225.446.519.542.847.051.163.083.33.097.5v0c.134-.068.277-.116.424-.144.315-.066.643-.015.922.144.293.182.526.443.673.754.078.149.138.307.178.471v0c.122-.089.256-.159.398-.208.3-.116.632-.116.931 0 .324.12.601.34.792.627.103.131.191.273.262.424v0c.104-.109.224-.202.356-.276a1.36 1.36 0 0 1 .914-.178c.334.07.642.232.889.466.124.116.235.246.33.386v0c.084-.124.185-.235.301-.33.236-.222.548-.345.871-.344.341.015.672.122.957.31a2.4 2.4 0 0 1 .394.317v0c.059-.137.139-.264.237-.377.193-.259.477-.435.795-.491.34-.046.686.002.999.14.15.067.292.151.424.25v0c.035-.15.094-.293.173-.424.147-.288.399-.509.703-.619.331-.09.68-.09 1.011 0 .165.042.324.102.475.178v0c.013-.145.049-.288.105-.423.108-.305.323-.559.606-.716.163-.081.339-.133.52-.153.158-.021.317-.021.474 0 .169.017.335.052.496.106v0a1.543 1.543 0 0 1 .042-.449c.072-.314.252-.593.508-.788.286-.189.621-.292.965-.297.169-.005.338.007.504.038v0a1.536 1.536 0 0 1 0-.449c.039-.322.188-.622.423-.847.256-.236.581-.384.927-.424.165-.028.333-.035.499-.021v0a1.555 1.555 0 0 1-.072-.449 1.332 1.332 0 0 1 .318-.881c.228-.261.536-.44.876-.509a2.34 2.34 0 0 1 .499-.067v0a1.39 1.39 0 0 1 .102-1.348c.205-.278.49-.486.817-.597.158-.061.323-.1.491-.119"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <svg
        x={'9.8em'}
        y={'15.4em'}
        width="21"
        height="29"
        viewBox="0 0 21 29"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.7429 12.0857C3.17786 11.3051 3.67895 10.5634 4.24057 9.86898C5.08195 8.69518 5.92333 7.53122 6.7647 6.37713C7.60608 5.25822 8.44744 4.13929 9.37716 3.08371C10.3616 1.94791 11.0347 1.20053 12.6291 1.14564C14.4807 1.03052 16.3393 1.11265 18.1738 1.39057C21.2111 1.97324 20.3024 5.57064 19.8817 7.72402C19.3559 10.4558 18.2663 12.947 17.3576 15.5564C16.8402 17.0469 16.2596 18.512 15.6454 19.9729C14.6442 22.3543 13.542 25.4619 11.59 27.2479C10.6518 28.1135 9.2888 27.9615 8.09825 27.953C6.64323 27.935 5.19234 27.7937 3.76098 27.5308C1.98568 27.2057 0.441758 26.7412 0.55955 24.6005C0.668929 22.7174 1.17374 20.8005 1.44299 18.9173C1.74167 16.8569 2.03616 14.7921 2.49471 12.757C2.55311 12.5249 2.63631 12.2998 2.7429 12.0857V12.0857Z"
          stroke="white"
          strokeMiterlimit="10"
        />
      </svg>
      <svg
        x={'10.05em'}
        y={'15.45em'}
        width="15"
        height="16"
        viewBox="0 0 15 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.42676 9.71746C1.47725 9.64569 1.5193 9.56544 1.56978 9.48944C2.33123 8.28186 3.25254 6.87156 3.67323 6.26777C4.34213 5.25864 5.01946 4.25796 5.74725 3.31216C6.52973 2.29036 7.06399 1.62327 8.30503 1.55149C9.74606 1.42864 11.1966 1.47961 12.6255 1.70344C14.9856 2.189 14.2325 5.39798 13.8876 7.32335C13.4963 9.73268 12.8051 12.0832 11.8304 14.3197C10.9259 16.2578 -0.521027 12.4113 1.42676 9.71746Z"
          stroke="white"
          strokeMiterlimit="10"
        />
      </svg>
      <svg
        x={'9.93em'}
        y={'16.2em'}
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.4255 5.25937C11.7061 7.42118 10.7175 10.1614 9.18203 11.7743C8.43741 12.5554 7.37728 12.4373 6.45177 12.4457C5.32265 12.4522 4.19556 12.3489 3.08626 12.1374C1.7064 11.8714 0.507402 11.4745 0.625195 9.5703C0.730367 7.88139 0.536873 1.54798 2.06818 1.34531C4.94989 0.96531 13.1743 3.02579 12.4255 5.25937Z"
          stroke="white"
          strokeMiterlimit="10"
        />
      </svg>

      <path
        d="M142.549 230.239s-1.719-2.597-6.845-3.36c-5.125-.762-2.052 8.432-1.735 9.228.317.797 1.27 2.907 4.118 1.174 2.849-1.733 6.379-4.479 4.462-7.042ZM125.508 258.944s1.414 2.542 5.778 3.114c4.363.572 1.921-8.474 1.667-9.241-.254-.766-1.011-2.851-3.492-1.029-2.48 1.822-5.536 4.665-3.953 7.156ZM143.797 256.33c.055.127-2.395 3.813-4.766 4.885-2.37 1.072-2.539-1.182-2.539-1.182l-.373-5.186c-.122-3.347 2.028-2.118 2.028-2.118 5.439 2.775 10.891 5.507 16.313 8.325 3.157 1.631 6.687 3.487 9.058-.352 2.67-4.317-1.837-6.592-4.944-8.626a3157.274 3157.274 0 0 1-10.205-6.724c-2.172-1.445-4.428-2.822-6.506-4.402-1.862-1.415.593-3.148.593-3.148l2.036-1.593s2.014-1.377 2.861.538c.846 1.915 1.397 6.932 1.194 7.758M127.662 230.002c-.525.221-.846.721-1.219 1.132-.372.41-.762.898-1.113 1.372a20.728 20.728 0 0 0-3.97 10.414 21.925 21.925 0 0 0-.055 3.39c.038.61.097 1.22.182 1.826.054.689.196 1.368.423 2.021.271.635.91 1.398 1.693 1.271 1.198-.208.5-1.601.356-2.313a15.64 15.64 0 0 1-.233-3.093c-.002-1.07.082-2.138.25-3.195a22.39 22.39 0 0 1 2.23-6.779 18.868 18.868 0 0 1 2.062-3.296c.423-.513 1.908-1.78 1.07-2.483a1.69 1.69 0 0 0-1.587-.288l-.089.021Z"
        strokeMiterlimit={10}
      />
    </g>
    <defs>
      <clipPath id="a">
        <path fill="#fff" d="M0 0h328v291H0z" />
      </clipPath>
    </defs>
  </svg>
);

export default BikeSvgComponent;
